import React, { useMemo } from "react";

import { useTheme } from "../../contexts/ThemeContext";

const ThemeCard = ({ themeKey, themeOption, currentTheme, changeTheme }) => (
  <button
    type="button"
    onClick={() => changeTheme(themeKey)}
    className={`relative p-4 rounded-lg border-2 transition-all text-left flex-shrink-0 w-[min(280px,85vw)] sm:w-auto snap-start ${
      currentTheme === themeKey
        ? "border-theme-accent shadow-accent-glow"
        : "border-theme-border hover:border-theme-accent/50"
    }`}
  >
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="font-display font-bold text-base text-theme-on-background leading-tight">{themeOption.name}</span>
      {currentTheme === themeKey ? (
        <span className="material-symbols-outlined text-theme-accent text-[22px] shrink-0">check_circle</span>
      ) : null}
    </div>
    <p className="font-mono text-[11px] text-theme-muted mb-3 leading-relaxed">{themeOption.description}</p>
    <div className="flex gap-1">
      <div className="flex-1 h-9 rounded border border-theme-border/50" style={{ backgroundColor: themeOption.panel }} title="Panel" />
      <div className="flex-1 h-9 rounded border border-theme-border/50" style={{ backgroundColor: themeOption.text }} title="Text" />
      <div className="flex-1 h-9 rounded border border-theme-border/50" style={{ backgroundColor: themeOption.accent }} title="Accent" />
      <div className="flex-1 h-9 rounded border border-theme-border/50" style={{ backgroundColor: themeOption.main }} title="Background" />
    </div>
  </button>
);

const SettingsPreferences = () => {
  const { currentTheme, changeTheme, themes } = useTheme();

  const { darkThemes, lightThemes } = useMemo(() => {
    const entries = Object.entries(themes);
    return {
      darkThemes: entries.filter(([, opt]) => opt.mode === "dark"),
      lightThemes: entries.filter(([, opt]) => opt.mode === "light"),
    };
  }, [themes]);

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">Preferences</h1>
        <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
          Visual theme for the app (saved on this device).
        </p>
      </div>

      <div className="rounded border border-theme-border bg-theme-panel p-5 md:p-6">
        <h2 className="font-display font-bold text-lg text-theme-on-background">Theme</h2>
        <p className="mt-1 font-mono text-[11px] text-theme-muted">Pick a palette. Your choice stays in local storage.</p>

        <p className="text-xs text-theme-muted mt-4 mb-2 md:hidden opacity-80">Swipe to compare themes</p>

        <div className="mt-6 space-y-8">
          <section>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.12em] text-theme-dim mb-3">Dark</h3>
            <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible snap-x snap-mandatory">
              {darkThemes.map(([key, themeOption]) => (
                <ThemeCard
                  key={key}
                  themeKey={key}
                  themeOption={themeOption}
                  currentTheme={currentTheme}
                  changeTheme={changeTheme}
                />
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.12em] text-theme-dim mb-3">Light</h3>
            <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible snap-x snap-mandatory">
              {lightThemes.map(([key, themeOption]) => (
                <ThemeCard
                  key={key}
                  themeKey={key}
                  themeOption={themeOption}
                  currentTheme={currentTheme}
                  changeTheme={changeTheme}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPreferences;
